import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  ChevronLeft,
  Check,
  X,
  Eye,
  Clock,
  FileText,
  User,
  Loader2,
  BookOpen,
  Pencil,
  Trash2,
  EyeOff,
  Wrench
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_MAP = {
  draft: { label: 'Brouillon', color: 'bg-slate-100 text-slate-600' },
  pending: { label: 'En attente', color: 'bg-amber-100 text-amber-700' },
  published: { label: 'Publié', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Refusé', color: 'bg-red-100 text-red-700' },
};

const AdminBlogPage = () => {
  const { user } = useAuth();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('pending');
  const [previewArticle, setPreviewArticle] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [actionLoading, setActionLoading] = useState(null);
  const [fixingExcerpts, setFixingExcerpts] = useState(false);

  const handleFixExcerpts = async () => {
    if (!window.confirm('Réparer automatiquement les extraits d\'articles corrompus (HTML brut) ?\n\nCette action est sûre : elle ne touche pas aux extraits SEO rédigés à la main.')) return;
    setFixingExcerpts(true);
    try {
      const res = await axios.post(`${API}/blog/admin/fix-excerpts`, {}, { withCredentials: true });
      const n = res.data?.fixed ?? 0;
      if (n > 0) {
        toast.success(`${n} extrait${n > 1 ? 's' : ''} réparé${n > 1 ? 's' : ''} avec succès !`);
        fetchArticles();
      } else {
        toast.info('Tous les extraits sont déjà propres. Rien à faire.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setFixingExcerpts(false);
    }
  };

  useEffect(() => { fetchArticles(); }, []);

  const fetchArticles = async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/all`, { withCredentials: true });
      setArticles(res.data.articles || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/blog/admin/articles/${id}/approve`, {}, { withCredentials: true });
      toast.success('Article publié !');
      fetchArticles();
      setPreviewArticle(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (id) => {
    setActionLoading(id);
    try {
      await axios.post(`${API}/blog/admin/articles/${id}/reject`, { reason: rejectReason }, { withCredentials: true });
      toast.success('Article refusé');
      fetchArticles();
      setPreviewArticle(null);
      setRejectReason('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleUnpublish = async (id) => {
    if (!window.confirm('Dépublier cet article ?')) return;
    setActionLoading(id);
    try {
      await axios.post(`${API}/blog/admin/articles/${id}/unpublish`, {}, { withCredentials: true });
      toast.success('Article dépublié');
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id, title) => {
    if (!window.confirm(`Supprimer définitivement "${title}" ?`)) return;
    setActionLoading(id);
    try {
      await axios.delete(`${API}/blog/admin/articles/${id}`, { withCredentials: true });
      toast.success('Article supprimé');
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(null);
    }
  };

  const filtered = articles.filter(a => filter === 'all' ? true : a.status === filter);

  const formatDate = (d) => {
    if (!d) return '-';
    return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <nav className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="flex items-center gap-2 text-slate-600 hover:text-slate-900">
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium">Dashboard</span>
            </Link>
            <h1 className="font-bold text-slate-900">Gestion du Blog</h1>
            <Button
              variant="outline"
              size="sm"
              onClick={handleFixExcerpts}
              disabled={fixingExcerpts}
              data-testid="fix-excerpts-btn"
              className="gap-2"
              title="Nettoyer les extraits d'articles contenant du HTML brut"
            >
              {fixingExcerpts ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wrench className="w-4 h-4" />}
              <span className="hidden sm:inline">Réparer les extraits</span>
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {['pending', 'published', 'draft', 'rejected'].map(status => {
            const count = articles.filter(a => a.status === status).length;
            const s = STATUS_MAP[status];
            return (
              <button
                key={status}
                onClick={() => setFilter(status)}
                className={`p-4 rounded-xl border text-left transition-all ${filter === status ? 'ring-2 ring-[#0D47A1] border-[#0D47A1]' : 'border-slate-200 hover:border-slate-300'}`}
              >
                <p className="text-2xl font-bold text-slate-900">{count}</p>
                <Badge className={s.color}>{s.label}</Badge>
              </button>
            );
          })}
        </div>

        {/* Articles List */}
        {loading ? (
          <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#0D47A1]" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16">
            <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-500">Aucun article dans cette catégorie</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filtered.map(article => (
              <Card key={article.article_id} className="border-0 shadow-sm hover:shadow-md transition-shadow">
                <CardContent className="p-4 flex items-center gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Badge className={STATUS_MAP[article.status]?.color}>{STATUS_MAP[article.status]?.label}</Badge>
                      <Badge variant="outline" className="text-xs">{article.category}</Badge>
                    </div>
                    <h3 className="font-bold text-slate-900 truncate">{article.title}</h3>
                    <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                      <span className="flex items-center gap-1"><User className="w-3 h-3" />{article.author_name}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{formatDate(article.updated_at)}</span>
                      <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{article.views || 0}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    {article.status === 'pending' && (
                      <>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(article.article_id)}
                          disabled={actionLoading === article.article_id}
                          className="bg-[#00B050] hover:bg-[#009040] text-white"
                          title="Approuver"
                          data-testid={`approve-${article.article_id}`}
                        >
                          {actionLoading === article.article_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setPreviewArticle(article)}
                          title="Refuser"
                          data-testid={`reject-${article.article_id}`}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {article.status === 'published' && (
                      <>
                        <Link to={`/blog/${article.slug}`}>
                          <Button size="sm" variant="outline" title="Voir"><Eye className="w-4 h-4" /></Button>
                        </Link>
                        <Link to={`/blog/write?edit=${article.article_id}`}>
                          <Button size="sm" variant="outline" title="Modifier"><Pencil className="w-4 h-4" /></Button>
                        </Link>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnpublish(article.article_id)}
                          disabled={actionLoading === article.article_id}
                          title="Dépublier"
                          className="text-amber-600 hover:bg-amber-50"
                        >
                          <EyeOff className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                    {article.status === 'draft' && (
                      <>
                        <Link to={`/blog/write?edit=${article.article_id}`}>
                          <Button size="sm" variant="outline" title="Modifier"><Pencil className="w-4 h-4" /></Button>
                        </Link>
                        <Button
                          size="sm"
                          onClick={() => handleApprove(article.article_id)}
                          disabled={actionLoading === article.article_id}
                          className="bg-[#00B050] hover:bg-[#009040] text-white"
                          title="Publier"
                        >
                          {actionLoading === article.article_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                        </Button>
                      </>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(article.article_id, article.title)}
                      disabled={actionLoading === article.article_id}
                      title="Supprimer"
                      className="text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Reject Modal */}
        {previewArticle && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setPreviewArticle(null)}>
            <Card className="max-w-lg w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-lg">Refuser l'article</CardTitle>
                <p className="text-sm text-slate-500">"{previewArticle.title}" par {previewArticle.author_name}</p>
              </CardHeader>
              <CardContent className="space-y-4">
                <textarea
                  placeholder="Raison du refus (optionnel)..."
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  className="w-full px-3 py-2 border rounded-lg text-sm resize-none h-24"
                />
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => setPreviewArticle(null)} className="flex-1">Annuler</Button>
                  <Button
                    variant="destructive"
                    onClick={() => handleReject(previewArticle.article_id)}
                    disabled={actionLoading === previewArticle.article_id}
                    className="flex-1"
                  >
                    {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
                    Confirmer le refus
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBlogPage;
